import React from 'react'
import { Divider, Popover } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { IconS24InfoCircle24 } from '@is24/cosma-ui-icons'
import { useAppSelector } from '../../../../../../../../app/hooks'
import { InitState } from '../../../../../../../../redux/reducers/root/initialState'
import styles from './RentPriceCheckDetails.module.css'

type RentPriceCheckDetailsProps = {
  price?: number
  livingSpace?: string
  constructionYear?: number | string
  rentControlApplies?: boolean | null
}

type DetailRowProps = {
  label: string
  value: React.ReactNode
  popoverContent: string
}

const toOptionalNumber = (value: string | undefined) => {
  if (!value) {
    return undefined
  }

  const parsedValue = Number(value.trim().replace(',', '.'))
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined
}

const toOptionalInteger = (value: number | string | undefined) => {
  if (value === undefined) {
    return undefined
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return undefined
  }

  const parsedValue = typeof value === 'number' ? value : Number(value.trim())
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : undefined
}

const formatCurrency = (value: number, language: string) =>
  `${value.toLocaleString(language, { maximumFractionDigits: 0 })} €`

const formatSquareMeterPrice = (value: number, livingArea: number, language: string) =>
  `${(value / livingArea).toLocaleString(language, { maximumFractionDigits: 2, minimumFractionDigits: 2 })} €/m²`

const formatPriceWithSquareMeterPrice = (value: number, livingArea: number | undefined, language: string) => {
  const formattedValue = formatCurrency(value, language)

  if (!livingArea) {
    return formattedValue
  }

  return `${formattedValue} (${formatSquareMeterPrice(value, livingArea, language)})`
}

const formatRangeWithSquareMeterPrice = (
  minValue: number,
  maxValue: number,
  livingArea: number | undefined,
  language: string
) => {
  const formattedRange = `${formatCurrency(minValue, language)} - ${formatCurrency(maxValue, language)}`

  if (!livingArea) {
    return formattedRange
  }

  return `${formattedRange} (${formatSquareMeterPrice(minValue, livingArea, language)} - ${formatSquareMeterPrice(
    maxValue,
    livingArea,
    language
  )})`
}

function DetailRow({ label, value, popoverContent }: DetailRowProps) {
  return (
    <div className='font-body-small-regular'>
      <div className={styles.row}>
        <div className={styles.rowLabel}>
          <span>{label}</span>
          <Popover.Root placement='bottom-start'>
            <Popover.Target as='button' className={styles.infoButton} aria-label={`${label} Info`}>
              <IconS24InfoCircle24 width={16} height={16} />
            </Popover.Target>
            <Popover.Content className={styles.popoverContent}>{popoverContent}</Popover.Content>
          </Popover.Root>
        </div>
        <span className={styles.rowValue}>{value}</span>
      </div>
    </div>
  )
}

export function RentPriceCheckDetails({
  price,
  livingSpace,
  constructionYear,
  rentControlApplies,
}: RentPriceCheckDetailsProps) {
  const { t, i18n } = useTranslation()
  const { minValue, maxValue } = useAppSelector((state: InitState) => state.valuationFeature)
  const rentIndexResult = useAppSelector((state: InitState) => state.rentIndexFeature)
  const livingArea = toOptionalNumber(livingSpace)
  const hasConstructionYear = toOptionalInteger(constructionYear) !== undefined
  const getRentIndexValue = () => {
    const rentIndexMinValue = rentIndexResult.minValue
    const rentIndexMaxValue = rentIndexResult.maxValue

    if (!hasConstructionYear && !livingArea) {
      return t('wizard.screen.basicInfo.price.recommendedNotification.check.missingConstructionYearAndLivingArea')
    }

    if (!hasConstructionYear) {
      return t('wizard.screen.basicInfo.price.recommendedNotification.check.missingConstructionYear')
    }

    if (!livingArea) {
      return t('wizard.screen.basicInfo.price.recommendedNotification.check.missingLivingArea')
    }

    if (rentIndexResult.status === 'notAvailable') {
      return t('wizard.screen.basicInfo.price.recommendedNotification.check.notAvailable')
    }

    if (rentIndexResult.status !== 'available' || rentIndexMinValue === undefined || rentIndexMaxValue === undefined) {
      return t('wizard.screen.basicInfo.price.recommendedNotification.check.noInformationAvailable')
    }

    return formatRangeWithSquareMeterPrice(rentIndexMinValue, rentIndexMaxValue, livingArea, i18n.language)
  }
  const getRentControlValue = () => {
    if (
      rentIndexResult.status !== 'available' ||
      rentIndexResult.minValue === undefined ||
      rentIndexResult.maxValue === undefined
    ) {
      return getRentIndexValue()
    }

    if (rentControlApplies === true) {
      return 'TBD'
    }

    if (rentControlApplies === false) {
      return t('wizard.screen.basicInfo.price.recommendedNotification.check.notAvailable')
    }

    return t('wizard.screen.basicInfo.price.recommendedNotification.check.noInformationAvailable')
  }
  const getAskingRentsValue = () => {
    if (minValue === undefined || maxValue === undefined) {
      return t('wizard.screen.basicInfo.price.recommendedNotification.check.noInformationAvailable')
    }

    return formatRangeWithSquareMeterPrice(minValue, maxValue, livingArea, i18n.language)
  }
  const rentIndexValue = getRentIndexValue()
  const rentControlValue = getRentControlValue()
  const askingRentsValue = getAskingRentsValue()

  return (
    <div className={styles.detailsTable}>
      <DetailRow
        label={t('wizard.screen.basicInfo.price.recommendedNotification.check.rentIndex')}
        value={rentIndexValue}
        popoverContent={t('wizard.screen.basicInfo.price.recommendedNotification.check.rentIndexPopover')}
      />
      <DetailRow
        label={t('wizard.screen.basicInfo.price.recommendedNotification.check.rentControl')}
        value={rentControlValue}
        popoverContent={t('wizard.screen.basicInfo.price.recommendedNotification.check.rentControlPopover')}
      />
      <DetailRow
        label={t('wizard.screen.basicInfo.price.recommendedNotification.check.askingRents')}
        value={askingRentsValue}
        popoverContent={t('wizard.screen.basicInfo.price.recommendedNotification.check.askingRentsPopover')}
      />
      <Divider />
      <div className={styles.row}>
        <span className='font-body-small-bold'>
          {t('wizard.screen.basicInfo.price.recommendedNotification.check.yourPrice')}
        </span>
        <span className='font-body-small-bold'>
          {price
            ? formatPriceWithSquareMeterPrice(price, livingArea, i18n.language)
            : t('wizard.screen.basicInfo.price.recommendedNotification.check.priceMissing')}
        </span>
      </div>
    </div>
  )
}

export const RentPriceCheckDetailsTestExports = {
  formatPriceWithSquareMeterPrice,
  formatRangeWithSquareMeterPrice,
  toOptionalInteger,
  toOptionalNumber,
}
